import { ORIGIN, API_ORIGIN, SITE_NAME, SITE_DESCRIPTION } from "@/components/Seo";

/**
 * Shared JSON-LD helpers for Fiable ou Pas.
 *
 * Important:
 * - Only use schema that matches visible page content.
 * - Do not add fake ratings, fake reviews, or product markup unless the page truly contains them.
 * - FAQ schema should only be used on pages where the FAQ is visibly present.
 */

const LOGO_URL = `${ORIGIN}/logo.png`;
const ORGANIZATION_ID = `${ORIGIN}/#organization`;
const WEBSITE_ID = `${ORIGIN}/#website`;
const WEBAPP_ID = `${ORIGIN}/#webapp`;

const OG_ORIGIN = API_ORIGIN || ORIGIN;

function cleanArray(items = []) {
  return items.filter(Boolean);
}

function stripMarkdown(text = "") {
  return String(text)
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/\[(.*?)\]\((.*?)\)/g, "$1")
    .replace(/#+\s/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function estimateWordCount(text = "") {
  const clean = stripMarkdown(text);
  if (!clean) return undefined;
  return clean.split(/\s+/).filter(Boolean).length;
}

/**
 * Organization schema
 * Use globally on important public pages, especially homepage.
 */
export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: SITE_NAME,
    url: ORIGIN,
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
    },
    description: SITE_DESCRIPTION,
    founder: {
      "@type": "Person",
      name: "David Ramaugé",
    },
    areaServed: {
      "@type": "Country",
      name: "France",
    },
    knowsLanguage: ["fr-FR"],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      availableLanguage: ["fr-FR"],
      url: `${ORIGIN}/contact`,
    },
    publishingPrinciples: `${ORIGIN}/a-propos`,
    sameAs: [],
  };
}

/**
 * Website schema
 * SearchAction intentionally removed:
 * /analyse/{domain} is an analyzer route, not a traditional site-search route.
 */
export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: SITE_NAME,
    url: ORIGIN,
    inLanguage: "fr-FR",
    description: SITE_DESCRIPTION,
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };
}

/**
 * WebApplication schema
 * Best used on the homepage because Fiable ou Pas is an online analysis tool.
 */
export function webApplicationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "@id": WEBAPP_ID,
    name: SITE_NAME,
    url: ORIGIN,
    applicationCategory: "SecurityApplication",
    operatingSystem: "Web",
    browserRequirements: "Requires JavaScript. Works in modern web browsers.",
    inLanguage: "fr-FR",
    description: SITE_DESCRIPTION,
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
    },
    creator: {
      "@id": ORGANIZATION_ID,
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
    featureList: [
      "Analyse automatique de sites e-commerce",
      "Score de confiance de 0 à 100",
      "Vérification HTTPS",
      "Détection des mentions légales",
      "Détection des CGV",
      "Analyse des coordonnées de contact",
      "Analyse des informations société",
      "Analyse de l'âge du domaine",
      "Vérification de signaux de réputation et de sécurité",
      "Recommandations pratiques avant achat",
    ],
  };
}

/**
 * FAQ schema
 * Use only when the FAQ is visibly present on the page.
 */
export function faqJsonLd(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: stripMarkdown(it.a),
      },
    })),
  };
}

/**
 * Breadcrumb schema
 */
export function breadcrumbJsonLd(crumbs = []) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${ORIGIN}${c.path}`,
    })),
  };
}

/**
 * Blog article schema
 * BlogPosting is more precise than generic Article for your blog content.
 */
export function articleJsonLd(post) {
  const url = `${ORIGIN}/blog/${post.slug}`;
  const image = `${ORIGIN}/og-default.png`;
  const wordCount = estimateWordCount(post.content);

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: post.title,
    description: post.excerpt,
    url,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    image: {
      "@type": "ImageObject",
      url: image,
      width: 1200,
      height: 630,
    },
    inLanguage: "fr-FR",
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: {
      "@type": "Organization",
      name: post.author || SITE_NAME,
      url: ORIGIN,
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    articleSection: post.category,
    keywords: (post.tags || []).join(", "),
    about: cleanArray([
      post.category && {
        "@type": "Thing",
        name: post.category,
      },
      ...(post.tags || []).slice(0, 8).map((tag) => ({
        "@type": "Thing",
        name: tag,
      })),
    ]),
    ...(wordCount ? { wordCount } : {}),
  };
}

/**
 * Blog listing page schema
 * Use on /blog with the posts returned by the API.
 */
export function blogCollectionJsonLd(posts = []) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${ORIGIN}/blog#collection`,
    name: "Blog — Guides et conseils pour acheter en ligne sereinement",
    description:
      "Articles pratiques, guides et conseils pour reconnaître les sites fiables, éviter les arnaques et acheter en ligne en toute sécurité.",
    url: `${ORIGIN}/blog`,
    inLanguage: "fr-FR",
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: posts.slice(0, 20).map((post, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${ORIGIN}/blog/${post.slug}`,
        name: post.title,
      })),
    },
  };
}

/**
 * About page schema
 * Use on /a-propos.
 */
export function aboutPageJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": `${ORIGIN}/a-propos#about`,
    name: "À propos — Notre mission",
    description:
      "Découvrez la mission de Fiable ou Pas, outil indépendant d'aide à la vérification des sites e-commerce.",
    url: `${ORIGIN}/a-propos`,
    inLanguage: "fr-FR",
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    about: {
      "@id": ORGANIZATION_ID,
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };
}

/**
 * Contact page schema
 * Use on /contact.
 */
export function contactPageJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${ORIGIN}/contact#contact`,
    name: "Contact — Fiable ou Pas",
    description:
      "Contactez Fiable ou Pas pour une question, une suggestion ou un signalement concernant l'analyse d'un site.",
    url: `${ORIGIN}/contact`,
    inLanguage: "fr-FR",
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    about: {
      "@id": ORGANIZATION_ID,
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };
}

/**
 * Method page schema
 * Use on /comment-ca-marche.
 */
export function howItWorksPageJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${ORIGIN}/comment-ca-marche#webpage`,
    name: "Comment ça marche — Notre méthode d'analyse",
    description:
      "Découvrez comment Fiable ou Pas analyse un site marchand : signaux automatiques, score de confiance et résultats actionnables.",
    url: `${ORIGIN}/comment-ca-marche`,
    inLanguage: "fr-FR",
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    about: {
      "@id": WEBAPP_ID,
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };
}

/**
 * Signals page schema
 * Use on /signaux.
 */
export function signalsPageJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${ORIGIN}/signaux#webpage`,
    name: "Les signaux analysés — Transparence du scoring",
    description:
      "Liste détaillée des signaux analysés par Fiable ou Pas : HTTPS, mentions légales, CGV, contact, contenu, identité commerciale et sécurité.",
    url: `${ORIGIN}/signaux`,
    inLanguage: "fr-FR",
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    about: {
      "@id": WEBAPP_ID,
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };
}

/**
 * Doubt/help page schema
 * Use on /en-cas-de-doute.
 */
export function doubtPageJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${ORIGIN}/en-cas-de-doute#webpage`,
    name: "Que faire en cas de doute ? — Guide pratique",
    description:
      "Guide pratique pour savoir quoi faire en cas de doute après un achat sur un site suspect : banque, preuves, signalement et ressources officielles.",
    url: `${ORIGIN}/en-cas-de-doute`,
    inLanguage: "fr-FR",
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    about: {
      "@type": "Thing",
      name: "Prévention des arnaques e-commerce",
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };
}